import { supabase } from '../../lib/supabase';
import type { Leaseholder } from '../../types';

// Define the types needed for the service
interface Sublease {
  id: string;
  landLot?: LandLot;
  leaseholder?: Leaseholder;
  status?: 'active' | 'pending' | 'expired';
  startDate?: Date | null;
  endDate?: Date | null;
  leaseType?: string | null;
  currentRental?: RentalContract | null;
}

interface LandLot {
  id: string;
  lotNumber: string;
  communityId: string;
  permittedUse: string | null;
  zoneType: string | null;
  isServiced: boolean;
  isAvailable: boolean;
}

interface RentalContract {
  id: string;
  amount: number;
  startDate: Date;
  frequency: string;
}

interface RentalContractRow {
  rental_trans_id: number;
  rental_amount: number;
  rental_start: string;
}

interface LandLotRow {
  lot_id: string;
  lot_number: string;
  lot_perm_use: string | null;
  lot_community: string;
  lot_zone: string | null;
  lot_serviced: string;
  lot_avail: string;
  community: {
    comm_id: string;
    comm_name: string;
  };
}

interface LeaseholderRow {
  holder_id: number;
  holder_name: string;
  holder_contact: string | null;
  holder_email: string | null;
  holder_phone: string | null;
  holder_abn: string | null;
}

interface SubleaseRow {
  lease_id: number;
  lease_start: string;
  lease_end: string;
  lease_method: string | null;
  lease_type: string | null;
  lease_rent_freq: string | null;
  lease_dealing: string | null;
  lease_holder: number;
  leaseholders: LeaseholderRow;
  lease_lots: {
    leaselot_lot: string;
    land_lots: LandLotRow;
  }[];
  rental_contract: RentalContractRow[] | null;
}

const mapLeaseholder = (leaseholder: LeaseholderRow): Leaseholder => ({
  holder_id: leaseholder.holder_id,
  holder_name: leaseholder.holder_name,
  holder_contact: leaseholder.holder_contact,
  holder_email: leaseholder.holder_email,
  holder_phone: leaseholder.holder_phone,
  holder_abn: leaseholder.holder_abn
});

const mapLandLot = (landLot: LandLotRow): LandLot => ({
  id: landLot.lot_id,
  lotNumber: landLot.lot_number,
  communityId: landLot.lot_community,
  permittedUse: landLot.lot_perm_use,
  zoneType: landLot.lot_zone,
  isServiced: landLot.lot_serviced === 'Y',
  isAvailable: landLot.lot_avail === 'Y'
});

const mapRentalContract = (rental: RentalContractRow, frequency: string | null): RentalContract => ({
  id: rental.rental_trans_id.toString(),
  amount: rental.rental_amount,
  startDate: new Date(rental.rental_start),
  frequency: frequency || 'annual'
});

// Define a simplified sublease type for dropdowns
interface SimpleSubleaseRow {
  lease_id: number;
  lot_id?: string;
  community_name?: string;
  holder_name?: string;
}

// Define a type for the enhanced sublease data
export interface EnhancedSublease {
  lease_id: number;
  lease_method: string | null;
  lease_type: string | null;
  lease_hmth_term: string | null;
  lease_dealing: string | null;
  lease_furth_term: string | null;
  lease_deal_doc: string | null;
  lease_holder: number | null;
  lease_start: string | null;
  lease_end: string | null;
  lease_rent_freq: string | null;
  leaseholder_name: string | null;
}

export const supabaseLeaseService = {
  // Get all subleases with leaseholder information
  getAllEnhanced: async (): Promise<EnhancedSublease[]> => {
    console.log('Fetching all subleases with leaseholder information...');
    
    // Check if user is authenticated
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.log('No authenticated session');
      return [];
    }
    
    try {
      // Query subleases with join to leaseholders using explicit FK reference
      // The !lease_holder tells Supabase which FK column to use for the join
      const { data, error } = await supabase
        .from('subleases')
        .select(`
          lease_id,
          lease_method,
          lease_type,
          lease_hmth_term,
          lease_dealing,
          lease_furth_term,
          lease_deal_doc,
          lease_holder,
          lease_start,
          lease_end,
          lease_rent_freq,
          lh:leaseholders!lease_holder(
            holder_name
          )
        `)
        .order('lease_id', { ascending: true });
      
      if (error) {
        console.error('Error fetching subleases:', error);
        return [];
      }
      
      if (!data || data.length === 0) {
        console.log('No subleases found');
        return [];
      }
      
      console.log(`Found ${data.length} subleases - raw data sample:`, data.slice(0, 3));
      
      // Cast data to the expected type - Supabase can't infer types with explicit FK references
      const subleaseData = data as unknown as Array<{
        lease_id: number;
        lease_method: string | null;
        lease_type: string | null;
        lease_hmth_term: string | null;
        lease_dealing: string | null;
        lease_furth_term: string | null;
        lease_deal_doc: string | null;
        lease_holder: number | null;
        lease_start: string | null;
        lease_end: string | null;
        lease_rent_freq: string | null;
        lh: { holder_name: string } | null;
      }>;
      
      // Transform the data to include leaseholder_name directly
      const enhancedSubleases: EnhancedSublease[] = subleaseData.map(sublease => {
        // Handle the case where lh (leaseholders) might be null or an object
        // Using alias 'lh' from the explicit FK join
        let leaseholderName = null;
        const lh = (sublease as any).lh;
        if (lh) {
          if (Array.isArray(lh) && lh.length > 0) {
            leaseholderName = lh[0].holder_name;
          } else if (typeof lh === 'object' && lh.holder_name) {
            leaseholderName = lh.holder_name;
          }
        }
        
        return {
          lease_id: sublease.lease_id,
          lease_method: sublease.lease_method,
          lease_type: sublease.lease_type,
          lease_hmth_term: sublease.lease_hmth_term,
          lease_dealing: sublease.lease_dealing,
          lease_furth_term: sublease.lease_furth_term,
          lease_deal_doc: sublease.lease_deal_doc,
          lease_holder: sublease.lease_holder,
          lease_start: sublease.lease_start,
          lease_end: sublease.lease_end,
          lease_rent_freq: sublease.lease_rent_freq,
          leaseholder_name: leaseholderName
        };
      });
      
      console.log(`Transformed ${enhancedSubleases.length} subleases with leaseholder names`);
      return enhancedSubleases;
    } catch (error) {
      console.error('Unexpected error in getAllEnhanced:', error);
      return [];
    }
  },
  
  // Get just the lease_ids for the underleases dropdown
  getSubleaseIds: async (): Promise<number[]> => {
    console.log('Fetching sublease IDs for dropdown...'); // Debug log
    
    // Check if user is authenticated
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.log('No authenticated session');
      return [];
    }
    
    try {
      // Simple direct query to get just the lease_id
      const { data, error } = await supabase
        .from('subleases')
        .select('lease_id');

      if (error) {
        console.error('Error fetching sublease IDs:', error);
        return [];
      }

      if (!data || data.length === 0) {
        console.log('No subleases found');
        return [];
      }

      // Return just the lease_ids - cast to expected type
      const typedData = data as unknown as Array<{ lease_id: number }>;
      return typedData.map(sublease => sublease.lease_id);
    } catch (error) {
      console.error('Error in getSubleaseIds:', error);
      return [];
    }
  },
  
  // Get just the lease_ids from the subleases table for dropdowns
  getAll: async (): Promise<SimpleSubleaseRow[]> => {
    console.log('Fetching lease_ids for dropdown...'); // Debug log
    
    // Check if user is authenticated
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.log('No authenticated session - returning mock data for development');
      // Return mock data for development/testing with just lease_id
      return [
        { lease_id: 1, lot_id: '', community_name: '', holder_name: '' },
        { lease_id: 2, lot_id: '', community_name: '', holder_name: '' },
        { lease_id: 3, lot_id: '', community_name: '', holder_name: '' }
      ];
    }
    
    try {
      // Direct query to get just the lease_ids
      console.log('Querying lease_ids from subleases table...');
      const { data, error } = await supabase
        .from('subleases')
        .select('lease_id');
      
      if (error) {
        console.error('Error fetching lease_ids:', error);
        // Return mock data as fallback
        return [
          { lease_id: 101, lot_id: '', community_name: '', holder_name: '' },
          { lease_id: 102, lot_id: '', community_name: '', holder_name: '' }
        ];
      }
      
      if (!data || data.length === 0) {
        console.log('No lease_ids found');
        return [];
      }
      
      console.log(`Found ${data.length} lease_ids:`, data);
      
      // Map to SimpleSubleaseRow format with just the lease_id - cast to expected type
      const typedData = data as unknown as Array<{ lease_id: number }>;
      return typedData.map(item => ({
        lease_id: item.lease_id,
        lot_id: '',
        community_name: '',
        holder_name: ''
      }));
    } catch (error) {
      console.error('Unexpected error in getAll for lease_ids:', error);
      // Return mock data as fallback
      return [
        { lease_id: 201, lot_id: '', community_name: '', holder_name: '' },
        { lease_id: 202, lot_id: '', community_name: '', holder_name: '' }
      ];
    }
  },
  
  // Original getAll method renamed to getAllDetailed
  getAllDetailed: async (): Promise<Sublease[]> => {
    console.log('Fetching detailed subleases...'); // Debug log
    
    // Check if user is authenticated
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.log('No authenticated session');
      return [];
    }
    
    // Get subleases with their relationships
    const { data: subleaseData, error: subleaseError } = await supabase
      .from('subleases')
      .select(`
        lease_id,
        lease_start,
        lease_end,
        lease_method,
        lease_type,
        lease_rent_freq,
        lease_dealing,
        lease_holder,
        leaseholders (
          holder_id,
          holder_name,
          holder_contact,
          holder_email,
          holder_phone,
          holder_abn
        ),
        lease_lots (
          leaselot_lot,
          land_lots (
            lot_id,
            lot_number,
            lot_perm_use,
            lot_community,
            lot_zone,
            lot_serviced,
            lot_avail,
            communities (
              comm_id,
              comm_name
            )
          )
        ),
        rental_contract (
          rental_trans_id,
          rental_amount,
          rental_start
        )
      `);

    if (subleaseError) {
      console.error('Supabase error:', subleaseError);
      throw subleaseError;
    }

    if (!subleaseData) {
      console.log('No sublease data returned');
      return [];
    }

    console.log('Raw sublease data:', subleaseData); // Debug log

    return (subleaseData as unknown as SubleaseRow[]).map(sublease => {
      // Get the first land lot from the lease_lots relationship
      const landLot = sublease.lease_lots?.[0]?.land_lots;

      // Get the most recent rental contract
      const currentRental = sublease.rental_contract
        ?.sort((a, b) => new Date(b.rental_start).getTime() - new Date(a.rental_start).getTime())[0];

      // Determine sublease status
      const now = new Date();
      const endDate = sublease.lease_end ? new Date(sublease.lease_end) : null;
      let status: 'active' | 'pending' | 'expired' = 'pending';
      
      if (endDate && endDate < now) {
        status = 'expired';
      } else if (sublease.lease_start && new Date(sublease.lease_start) <= now) {
        status = 'active';
      }

      return {
        id: sublease.lease_id.toString(),
        landLot: landLot ? mapLandLot(landLot) : {
          id: '',
          lotNumber: '',
          communityId: '',
          permittedUse: null,
          zoneType: null,
          isServiced: false,
          isAvailable: false
        },
        leaseholder: sublease.leaseholders ? mapLeaseholder(sublease.leaseholders) : {
          holder_id: 0,
          holder_name: '',
          holder_contact: null,
          holder_email: null,
          holder_phone: null,
          holder_abn: null
        },
        status,
        startDate: sublease.lease_start ? new Date(sublease.lease_start) : null,
        endDate: sublease.lease_end ? new Date(sublease.lease_end) : null,
        leaseType: sublease.lease_type || null,
        currentRental: currentRental ? mapRentalContract(currentRental, sublease.lease_rent_freq) : null
      };
    });
  },

  getById: async (id: string): Promise<Sublease | null> => {
    // Check if user is authenticated
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.log('No authenticated session');
      return null;
    }

    // Get sublease with relationships
    const { data: sublease, error: subleaseError } = await supabase
      .from('subleases')
      .select(`
        lease_id,
        lease_start,
        lease_end,
        lease_method,
        lease_type,
        lease_rent_freq,
        lease_dealing,
        lease_holder,
        leaseholders (
          holder_id,
          holder_name,
          holder_contact,
          holder_email,
          holder_phone,
          holder_abn
        ),
        lease_lots (
          leaselot_lot,
          land_lots (
            lot_id,
            lot_number,
            lot_perm_use,
            lot_community,
            lot_zone,
            lot_serviced,
            lot_avail,
            communities (
              comm_id,
              comm_name
            )
          )
        ),
        rental_contract (
          rental_trans_id,
          rental_amount,
          rental_start
        )
      `)
      .eq('lease_id', id)
      .single();

    if (subleaseError) {
      console.error('Supabase error:', subleaseError);
      throw subleaseError;
    }

    if (!sublease) return null;

    const subleaseRow = sublease as unknown as SubleaseRow;

    // Get the first land lot from the lease_lots relationship
    const landLot = subleaseRow.lease_lots?.[0]?.land_lots;

    // Get the most recent rental contract
    const currentRental = subleaseRow.rental_contract
      ?.sort((a, b) => new Date(b.rental_start).getTime() - new Date(a.rental_start).getTime())[0];

    // Determine sublease status
    const now = new Date();
    const endDate = subleaseRow.lease_end ? new Date(subleaseRow.lease_end) : null;
    let status: 'active' | 'pending' | 'expired' = 'pending';
    
    if (endDate && endDate < now) {
      status = 'expired';
    } else if (subleaseRow.lease_start && new Date(subleaseRow.lease_start) <= now) {
      status = 'active';
    }

    return {
      id: subleaseRow.lease_id.toString(),
      landLot: landLot ? mapLandLot(landLot) : {
        id: '',
        lotNumber: '',
        communityId: '',
        permittedUse: null,
        zoneType: null,
        isServiced: false,
        isAvailable: false
      },
      leaseholder: subleaseRow.leaseholders ? mapLeaseholder(subleaseRow.leaseholders) : {
        holder_id: 0,
        holder_name: '',
        holder_contact: null,
        holder_email: null,
        holder_phone: null,
        holder_abn: null
      },
      status,
      startDate: subleaseRow.lease_start ? new Date(subleaseRow.lease_start) : null,
      endDate: subleaseRow.lease_end ? new Date(subleaseRow.lease_end) : null,
      leaseType: subleaseRow.lease_type || null,
      currentRental: currentRental ? mapRentalContract(currentRental, subleaseRow.lease_rent_freq) : null
    };
  }
};
